import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { useLang } from "./i18n";
import { v4 as uuidv4 } from "uuid"; // 1. Add this import

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ConciergeWidget() {
  const { t, lang, images } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(uuidv4());
  const scrollRef = useRef(null);

  useEffect(() => {
    setMessages([{ role: "assistant", content: t.concierge.greeting }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const { token } = JSON.parse(payload);
            if (token) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t.concierge.errorMsg };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const showTyping = busy && messages.length > 0 && messages[messages.length - 1].content === "";

  return (
    <div className="fixed bottom-5 right-5 z-50 sm:bottom-7 sm:right-7">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mb-4 flex h-[520px] max-h-[72vh] w-[380px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_32px_90px_rgba(0,0,0,0.35)]"
          >
            <div className="flex items-center gap-3 bg-navy px-5 py-4">
              <img src={images.logoMark} alt="Settld" className="h-9 w-9 rounded-lg" />
              <div className="flex-1">
                <div className="text-sm font-extrabold text-white">{t.concierge.title}</div>
                <div className="flex items-center gap-1.5 text-[11px] text-mist">
                  <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-aqua-400" />
                  {t.concierge.subtitle}
                </div>
              </div>
              <button
                data-testid="concierge-close-btn"
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Close chat"
              >
                <X size={17} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-paper px-4 py-4" data-lenis-prevent>
              {messages.map((m, i) => (
                <div key={i} data-testid={`concierge-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.content !== "" && (
                    <div
                      className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
                        m.role === "user"
                          ? "rounded-br-md bg-navy text-white"
                          : "rounded-bl-md border border-slate-200 bg-white text-ink shadow-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                  )}
                </div>
              ))}
              {showTyping && (
                <div className="flex justify-start" data-testid="concierge-typing">
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="typing-dot h-1.5 w-1.5 rounded-full bg-navy/50" style={{ animationDelay: `${d * 0.18}s` }} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-slate-200 bg-white p-3">
              <input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.concierge.placeholder}
                className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-ink outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-aqua-600 focus:ring-4 focus:ring-aqua-600/10"
              />
              <button
                data-testid="concierge-send-btn"
                onClick={send}
                disabled={busy || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-maple text-white transition-colors duration-200 hover:bg-[#C23225] disabled:opacity-50"
                aria-label={t.concierge.send}
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="concierge-open-btn"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="relative ml-auto flex h-14 w-14 items-center justify-center rounded-full bg-maple text-white shadow-[0_14px_40px_rgba(217,58,43,0.45)]"
        aria-label={t.concierge.title}
      >
        {!open && <span className="pulse-dot absolute inset-0 rounded-full border-2 border-maple" />}
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>
    </div>
  );
}
